import {
  differenceInDays,
  differenceInMinutes,
  format,
  isAfter,
  isBefore,
  parseISO,
} from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';

export default class Coupon {
  /** @type {string} */
  id = '';

  /** @type {string} */
  code = '';

  /** @type {string} */
  claimedAt = '';

  /** @type {string} */
  redeemedAt = '';

  /** @type {"unclaimed" | "claimed" | "redeemed" | "part_used" | "revoke" | "suspended"} */
  status = '';

  /** @type {string} */
  name = '';

  /** @type {string|null} */
  description = '';

  /** @type {"money" | "rate"} */
  discountType = '';

  /** @type {Array<{min: string, discount: string}>} */
  discountTiers = [];

  /** @type {number} */
  maxDiscountAmount = 0;

  /** @type {boolean} */
  stackable = false;

  /** @type {string} */
  redeemStartAt = '';

  /** @type {string} */
  redeemEndAt = '';

  /** @type {string} */
  currency = '';

  /** @type {string|null} */
  country = '';

  /** @type {string} */
  timeZone = '';

  /**
   * @param {Object} params
   * @param {string} params.id
   * @param {string} params.code
   * @param {string} params.claimedAt
   * @param {string} params.redeemedAt
   * @param {"unclaimed" | "claimed" | "redeemed" | "part_used" | "revoke" | "suspended"} params.status
   * @param {string} params.name
   * @param {string|null} params.description
   * @param {"money" | "rate"} params.discountType
   * @param {Array<{min: string, discount: string}>} params.discountTiers
   * @param {number} params.maxDiscountAmount
   * @param {boolean} params.stackable
   * @param {string | null} params.redeemStartAt
   * @param {string | null} params.redeemEndAt
   * @param {string} params.currency
   * @param {string|null} params.country
   * @param {string} params.timeZone
   */
  constructor({
    id,
    code,
    claimedAt,
    redeemedAt,
    status,
    name,
    description,
    discountType,
    discountTiers,
    maxDiscountAmount,
    stackable,
    redeemStartAt,
    redeemEndAt,
    currency,
    country,
    timeZone,
  }) {
    this.id = id;
    this.code = code;
    this.claimedAt = claimedAt;
    this.redeemedAt = redeemedAt;
    this.status = status;
    this.name = name;
    this.description = description;
    this.discountType = discountType;
    this.discountTiers = discountTiers;
    this.maxDiscountAmount = maxDiscountAmount;
    this.stackable = stackable;
    this.redeemStartAt = redeemStartAt;
    this.redeemEndAt = redeemEndAt;
    this.currency = currency;
    this.country = country;
    this.timeZone = timeZone;
  }

  get claimedAtUTC() {
    if (!this.claimedAt) return null;

    return fromZonedTime(this.claimedAt, this.timeZone);
  }

  get redeemedAtUTC() {
    if (!this.redeemedAt) return null;

    return fromZonedTime(this.redeemedAt, this.timeZone);
  }

  get redeemStartAtUTC() {
    if (!this.redeemStartAt) return null;

    return fromZonedTime(this.redeemStartAt, this.timeZone);
  }

  get redeemEndAtUTC() {
    if (!this.redeemEndAt) return null;

    return fromZonedTime(this.redeemEndAt, this.timeZone);
  }

  get baseDiscountAmount() {
    return this.discountTiers[0].discount;
  }

  get formattedDiscountAmount() {
    if (this.discountType === 'rate') {
      return this.baseDiscountAmount.split('.')[1];
    }

    return this.baseDiscountAmount;
  }

  get formattedExpiredAt() {
    if (!this.redeemEndAtUTC) return null;

    return format(this.redeemEndAtUTC, 'yyyy.MM.dd HH:mm');
  }

  get formattedRedeemStartAt() {
    if (!this.redeemStartAtUTC) return null;

    return format(this.redeemStartAtUTC, 'yyyy.MM.dd HH:mm');
  }

  get isRedeemStarted() {
    if (!this.redeemStartAtUTC) return true;
    return isBefore(this.redeemStartAtUTC, new Date());
  }

  get isRedeemExpired() {
    if (!this.redeemEndAtUTC) return false;

    return isAfter(new Date(), this.redeemEndAtUTC);
  }

  get isRedeemable() {
    return this.isRedeemStarted && !this.isRedeemExpired;
  }

  get isAvailable() {
    return this.isRedeemable && (this.status === 'claimed' || this.status === 'part_used');
  }

  get isNew() {
    return differenceInMinutes(fromZonedTime(this.claimedAt, this.timeZone), new Date()) < 1;
  }

  get expireInDays() {
    const days = differenceInDays(fromZonedTime(this.redeemEndAt, this.timeZone), new Date());
    return days;
  }

  update(props) {
    Object.assign(this, props);
  }
}
